import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { adminApi } from "@/lib/api";
import ConstantContactConnection from "./ConstantContactConnection";
import UserManagement from "./UserManagement";
import { BRAND } from "@/data/content";
import {
  Users, CalendarClock, Mail, Activity, LogOut, X, ChevronRight, TrendingUp, Eye, MousePointerClick, Settings, FileText, Edit3, Plus, Image as ImageIcon,
} from "lucide-react";

const STATUS_COLORS = {
  new: "bg-rc-red/10 text-rc-red border-rc-red/30",
  contacted: "bg-blue-50 text-blue-700 border-blue-200",
  qualified: "bg-amber-50 text-amber-700 border-amber-200",
  won: "bg-green-50 text-green-700 border-green-200",
  lost: "bg-gray-100 text-gray-500 border-gray-200",
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  confirmed: "bg-blue-50 text-blue-700 border-blue-200",
  completed: "bg-green-50 text-green-700 border-green-200",
  cancelled: "bg-gray-100 text-gray-500 border-gray-200",
};

const ADMIN_SCOPES = ["overview", "leads", "appointments", "subscribers", "content", "vanguard", "edit_requests", "settings"];

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

export default function Dashboard() {
  const { admin, ready, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).has("cc_callback") || new URLSearchParams(window.location.search).get("tab") === "settings" ? "settings" : "overview");
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [appts, setAppts] = useState([]);
  const [subs, setSubs] = useState([]);
  const [siteContent, setSiteContent] = useState(null);
  const [editRequests, setEditRequests] = useState([]);
  const [vanguardIssues, setVanguardIssues] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [alertSettings, setAlertSettings] = useState(null);
  const [constantContact, setConstantContact] = useState(null);
  const [detail, setDetail] = useState(null);
  const scopes = Array.isArray(admin?.scopes) ? admin.scopes : ADMIN_SCOPES;

  useEffect(() => {
    if (ready && !admin && !localStorage.getItem("rc_admin_token")) navigate("/admin/login");
  }, [ready, admin, navigate]);

  const load = useCallback(async () => {
    try {
      const get = (scope, path, fallback) => scopes.includes(scope) ? adminApi.get(path) : Promise.resolve({ data: fallback });
      const [s, l, a, sub, content, edits, issues, users, alerts, cc] = await Promise.all([
        get("overview", "/admin/stats", null),
        get("leads", "/admin/leads", []),
        get("appointments", "/admin/appointments", []),
        get("subscribers", "/admin/subscribers", []),
        get("content", "/admin/site-content", null),
        get("edit_requests", "/admin/edit-requests", []),
        get("vanguard", "/admin/vanguard/issues", []),
        get("settings", "/admin/users", []),
        get("settings", "/admin/settings/alerts", null),
        get("settings", "/admin/settings/constant-contact", null),
      ]);
      setStats(s.data);
      setLeads(l.data);
      setAppts(a.data);
      setSubs(sub.data);
      setSiteContent(content.data);
      setEditRequests(edits.data);
      setVanguardIssues(issues.data);
      setAdminUsers(users.data);
      setAlertSettings(alerts.data);
      setConstantContact(cc.data);
    } catch (e) {
      if (e?.response?.status === 401) navigate("/admin/login");
    }
  }, [navigate, scopes]);

  useEffect(() => {
    load();
  }, [load]);

  const openLead = async (id) => {
    const { data } = await adminApi.get(`/admin/leads/${id}`);
    setDetail(data);
  };

  const updateLead = async (id, patch) => {
    await adminApi.patch(`/admin/leads/${id}`, patch);
    await load();
    if (detail) openLead(id);
  };

  const updateAppt = async (id, patch) => {
    await adminApi.patch(`/admin/appointments/${id}`, patch);
    await load();
  };

  const updateEditRequest = async (id, patch) => {
    await adminApi.patch(`/admin/edit-requests/${id}`, patch);
    await load();
  };

  const createEditRequest = async (payload) => {
    if (payload instanceof FormData) {
      await adminApi.post("/admin/edit-requests/upload", payload, { headers: { "Content-Type": "multipart/form-data" } });
    } else {
      await adminApi.post("/admin/edit-requests", payload);
    }
    await load();
  };

  const saveSiteContent = async (patch) => {
    const { data } = await adminApi.patch("/admin/site-content", patch);
    setSiteContent(data);
  };

  const createVanguardIssue = async (payload) => {
    await adminApi.post("/admin/vanguard/issues", payload);
    await load();
  };

  const updateVanguardIssue = async (id, patch) => {
    await adminApi.patch(`/admin/vanguard/issues/${id}`, patch);
    await load();
  };

  const uploadVanguardCover = async (id, file) => {
    const data = new FormData();
    data.append("file", file);
    await adminApi.post(`/admin/vanguard/issues/${id}/cover`, data, { headers: { "Content-Type": "multipart/form-data" } });
    await load();
  };

  const createAdminUser = async (payload) => {
    await adminApi.post("/admin/users", payload);
    await load();
  };

  const updateAdminUser = async (email, payload) => {
    await adminApi.patch(`/admin/users/${encodeURIComponent(email)}`, payload);
    await load();
  };

  const removeAdminUser = async (email) => {
    await adminApi.delete(`/admin/users/${encodeURIComponent(email)}`);
    await load();
  };

  const saveAlertSettings = async (payload) => {
    const { data } = await adminApi.put("/admin/settings/alerts", payload);
    setAlertSettings(data);
  };

  const saveConstantContact = async (payload) => {
    const { data } = await adminApi.put("/admin/settings/constant-contact", payload);
    setConstantContact(data);
  };

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const TABS = [
    { key: "overview", scope: "overview", label: "Overview", icon: TrendingUp },
    { key: "leads", scope: "leads", label: "Leads", icon: Users },
    { key: "appointments", scope: "appointments", label: "Appointments", icon: CalendarClock },
    { key: "subscribers", scope: "subscribers", label: "Subscribers", icon: Mail },
    { key: "content", scope: "content", label: "Site Content", icon: Settings },
    { key: "vanguard", scope: "vanguard", label: "Vanguard", icon: FileText },
    { key: "edit-requests", scope: "edit_requests", label: "Edit Requests", icon: Edit3 },
    { key: "settings", scope: "settings", label: "Settings", icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex">
      {/* Sidebar */}
      <aside className="w-64 bg-rc-charcoal text-white flex-col hidden md:flex fixed h-full">
        <div className="p-6 border-b border-white/10">
          <img src={BRAND.logoLight} alt="Ranger" className="h-11 w-auto" />
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-rc-sand mt-3">Lead Manager</div>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {TABS.filter((t) => scopes.includes(t.scope)).map((t) => (
            <button
              key={t.key}
              data-testid={`tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-semibold transition-colors ${
                tab === t.key ? "bg-rc-red text-white" : "text-white/60 hover:text-white hover:bg-white/5"
              }`}
            >
              <t.icon size={17} /> {t.label}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="text-white/50 text-xs mb-2 font-mono truncate">{admin?.email}</div>
          <button
            data-testid="logout-button"
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-white/70 hover:text-white hover:bg-white/5"
          >
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 md:ml-64 p-6 lg:p-10">
        {/* mobile tabs */}
        <div className="md:hidden flex gap-2 mb-6 overflow-x-auto no-scrollbar">
          {TABS.filter((t) => scopes.includes(t.scope)).map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-sm font-semibold whitespace-nowrap ${
                tab === t.key ? "bg-rc-red text-white" : "bg-white border border-black/10"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "overview" && (
          <div data-testid="overview-tab">
            <h1 className="font-heading font-black text-3xl text-rc-charcoal mb-1">Site Analytics</h1>
            <p className="text-gray-500 mb-8">First-party analytics from Ranger site sessions and activity events. No Google Analytics.</p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={Activity} label="Sessions" value={stats?.analytics?.sessions || 0} sub={`${stats?.analytics?.visitors || 0} visitors`} />
              <StatCard icon={Eye} label="Page Views" value={stats?.analytics?.page_views || 0} sub={`${stats?.analytics?.pages_per_session || 0} pages/session`} />
              <StatCard icon={TrendingUp} label="Avg. Time / Page" value={formatDuration(stats?.analytics?.avg_time_on_page_seconds || 0)} sub="estimated from page transitions" />
              <StatCard icon={MousePointerClick} label="Events" value={stats?.events || 0} sub="all first-party activity" />
            </div>

            <div className="mt-8 grid xl:grid-cols-2 gap-6">
              <AnalyticsPanel title="Top Pages" rows={stats?.analytics?.top_pages || []} columns={["Page", "Views"]} render={(r) => [r.path, r.views]} />
              <AnalyticsPanel title="Landing Pages" rows={stats?.analytics?.landing_pages || []} columns={["Landing Page", "Sessions"]} render={(r) => [r.path, r.sessions]} />
              <AnalyticsPanel title="Top CTAs / Clicks" rows={stats?.analytics?.top_ctas || []} columns={["CTA", "Clicks"]} render={(r) => [r.label, r.count]} />
              <AnalyticsPanel title="Event Types" rows={stats?.analytics?.event_types || []} columns={["Type", "Count"]} render={(r) => [r.type, r.count]} />
            </div>

            <div className="mt-6 bg-white border border-[#E5E7EB] rounded-md overflow-hidden">
              <div className="px-5 py-4 border-b border-[#E5E7EB] font-heading font-bold text-rc-charcoal">Last 14 Days</div>
              <table className="w-full text-sm">
                <thead className="bg-[#F9FAFB] text-gray-500 text-xs uppercase tracking-wide"><tr><Th>Date</Th><Th>Sessions</Th><Th>Page Views</Th><Th>Events</Th></tr></thead>
                <tbody>
                  {(stats?.analytics?.daily || []).map((d) => <tr key={d.date} className="border-t border-[#E5E7EB]"><Td>{d.date}</Td><Td>{d.sessions}</Td><Td>{d.page_views}</Td><Td>{d.events}</Td></tr>)}
                  {(!stats?.analytics?.daily || stats.analytics.daily.length === 0) && <tr><Td colSpan={4}><span className="text-gray-400">No analytics data yet.</span></Td></tr>}
                </tbody>
              </table>
            </div>

            <div className="mt-6 bg-white border border-[#E5E7EB] rounded-md overflow-hidden">
              <div className="px-5 py-4 border-b border-[#E5E7EB] font-heading font-bold text-rc-charcoal">Recent Activity</div>
              <div className="divide-y divide-[#E5E7EB]">
                {(stats?.analytics?.recent_events || []).slice(0, 12).map((e, i) => (
                  <div key={e.id || i} className="px-5 py-3 text-sm flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
                    <span className="font-mono text-xs text-gray-400 shrink-0">{fmt(e.ts)}</span>
                    <span className="font-semibold text-rc-charcoal">{e.type}</span>
                    <span className="text-gray-500 truncate">{e.label || e.path || "—"}</span>
                  </div>
                ))}
                {(!stats?.analytics?.recent_events || stats.analytics.recent_events.length === 0) && <div className="px-5 py-6 text-gray-400 text-sm">No recent events.</div>}
              </div>
            </div>
          </div>
        )}

        {tab === "leads" && (
          <div data-testid="leads-tab">
            <h1 className="font-heading font-black text-3xl text-rc-charcoal mb-1">Leads</h1>
            <p className="text-gray-500 mb-8">Click a lead to see their browsing insight & update status.</p>
            <div className="bg-white border border-[#E5E7EB] rounded-md">
              <LeadTable leads={leads} onOpen={openLead} testid="admin-leads-table" />
            </div>
          </div>
        )}

        {tab === "appointments" && (
          <div data-testid="appointments-tab">
            <h1 className="font-heading font-black text-3xl text-rc-charcoal mb-1">Appointments</h1>
            <p className="text-gray-500 mb-8">Requests captured via the chatbot and forms.</p>
            <div className="bg-white border border-[#E5E7EB] rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#F9FAFB] text-gray-500 text-xs uppercase tracking-wide">
                  <tr>
                    <Th>Name</Th><Th>Contact</Th><Th>Preferred</Th><Th>Topic</Th><Th>Status</Th>
                  </tr>
                </thead>
                <tbody>
                  {appts.map((a) => (
                    <tr key={a.id} className="border-t border-[#E5E7EB]">
                      <Td><div className="font-semibold text-rc-charcoal">{a.name}</div><div className="text-gray-400 text-xs">{a.source}</div></Td>
                      <Td><div>{a.email}</div><div className="text-gray-400 text-xs">{a.phone}</div></Td>
                      <Td>{a.preferred_date} {a.preferred_time}</Td>
                      <Td>{a.topic || "—"}</Td>
                      <Td>
                        <select
                          data-testid={`appt-status-${a.id}`}
                          value={a.status}
                          onChange={(e) => updateAppt(a.id, { status: e.target.value })}
                          className={`text-xs border px-2 py-1 rounded ${STATUS_COLORS[a.status] || ""}`}
                        >
                          {["pending", "confirmed", "completed", "cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </Td>
                    </tr>
                  ))}
                  {appts.length === 0 && <tr><Td colSpan={5}><span className="text-gray-400">No appointments yet.</span></Td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "subscribers" && (
          <div data-testid="subscribers-tab">
            <h1 className="font-heading font-black text-3xl text-rc-charcoal mb-1">Newsletter Subscribers</h1>
            <p className="text-gray-500 mb-8">The Vanguard News Magazine sign-ups.</p>
            <div className="bg-white border border-[#E5E7EB] rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#F9FAFB] text-gray-500 text-xs uppercase tracking-wide">
                  <tr><Th>Email</Th><Th>Subscribed</Th></tr>
                </thead>
                <tbody>
                  {subs.map((s) => (
                    <tr key={s.id} className="border-t border-[#E5E7EB]">
                      <Td className="font-medium text-rc-charcoal">{s.email}</Td>
                      <Td>{fmt(s.created_at)}</Td>
                    </tr>
                  ))}
                  {subs.length === 0 && <tr><Td colSpan={2}><span className="text-gray-400">No subscribers yet.</span></Td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}


        {tab === "content" && (
          <SiteContentTab content={siteContent} onSave={saveSiteContent} />
        )}

        {tab === "vanguard" && (
          <VanguardTab issues={vanguardIssues} onCreate={createVanguardIssue} onUpdate={updateVanguardIssue} onUploadCover={uploadVanguardCover} />
        )}

        {tab === "edit-requests" && (
          <EditRequestsTab requests={editRequests} onUpdate={updateEditRequest} onCreate={createEditRequest} />
        )}

        {tab === "settings" && (
          <SettingsTab onConnected={setConstantContact} currentEmail={admin.email} onRemoveUser={removeAdminUser} users={adminUsers} alerts={alertSettings} constantContact={constantContact} onCreateUser={createAdminUser} onUpdateUser={updateAdminUser} onSaveAlerts={saveAlertSettings} onSaveConstantContact={saveConstantContact} />
        )}
      </main>

      {/* Lead detail drawer */}
      {detail && (
        <LeadDrawer detail={detail} onClose={() => setDetail(null)} onUpdate={updateLead} />
      )}
    </div>
  );
}

function formatDuration(seconds) {
  const s = Number(seconds || 0);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r ? `${m}m ${r}s` : `${m}m`;
}

function AnalyticsPanel({ title, rows, columns, render }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-md overflow-hidden">
      <div className="px-5 py-4 border-b border-[#E5E7EB] font-heading font-bold text-rc-charcoal">{title}</div>
      <table className="w-full text-sm">
        <thead className="bg-[#F9FAFB] text-gray-500 text-xs uppercase tracking-wide"><tr>{columns.map((c) => <Th key={c}>{c}</Th>)}</tr></thead>
        <tbody>
          {rows.map((row, idx) => {
            const cells = render(row);
            return <tr key={idx} className="border-t border-[#E5E7EB]">{cells.map((cell, i) => <Td key={i} className={i === 0 ? "font-medium text-rc-charcoal" : ""}>{cell}</Td>)}</tr>;
          })}
          {rows.length === 0 && <tr><Td colSpan={columns.length}><span className="text-gray-400">No data yet.</span></Td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-md p-5">
      <div className="flex items-center justify-between">
        <span className="text-gray-500 text-xs uppercase tracking-wide font-mono">{label}</span>
        <Icon size={16} className="text-rc-red" />
      </div>
      <div className="font-heading font-black text-3xl text-rc-charcoal mt-3">{value ?? "—"}</div>
      {sub && <div className="text-gray-400 text-xs mt-1">{sub}</div>}
    </div>
  );
}

function Th({ children }) {
  return <th className="text-left px-4 py-3 font-semibold">{children}</th>;
}
function Td({ children, colSpan, className = "" }) {
  return <td colSpan={colSpan} className={`px-4 py-3 align-top ${className}`}>{children}</td>;
}

function LeadTable({ leads, onOpen, testid }) {
  return (
    <table className="w-full text-sm" data-testid={testid}>
      <thead className="bg-[#F9FAFB] text-gray-500 text-xs uppercase tracking-wide">
        <tr><Th>Name</Th><Th>Contact</Th><Th>Source</Th><Th>Activity</Th><Th>Status</Th><Th></Th></tr>
      </thead>
      <tbody>
        {leads.map((l) => (
          <tr
            key={l.id}
            data-testid={`lead-row-${l.id}`}
            onClick={() => onOpen(l.id)}
            className="border-t border-[#E5E7EB] hover:bg-[#F9FAFB] cursor-pointer"
          >
            <Td><div className="font-semibold text-rc-charcoal">{l.name}</div><div className="text-gray-400 text-xs">{fmt(l.created_at)}</div></Td>
            <Td><div>{l.email}</div><div className="text-gray-400 text-xs">{l.phone || "—"}</div></Td>
            <Td><span className="text-xs font-mono text-gray-500">{l.source}</span></Td>
            <Td><span className="inline-flex items-center gap-1 text-xs text-gray-600"><Activity size={12} /> {l.activity_count}</span></Td>
            <Td><span className={`text-xs border px-2 py-1 rounded ${STATUS_COLORS[l.status] || ""}`}>{l.status}</span></Td>
            <Td><ChevronRight size={16} className="text-gray-300" /></Td>
          </tr>
        ))}
        {leads.length === 0 && <tr><Td colSpan={6}><span className="text-gray-400">No leads yet.</span></Td></tr>}
      </tbody>
    </table>
  );
}

function LeadDrawer({ detail, onClose, onUpdate }) {
  const { lead, events, summary, appointments } = detail;
  const [notes, setNotes] = useState(lead.notes || "");

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="lead-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-rc-charcoal text-white px-6 py-5 flex items-center justify-between">
          <div>
            <div className="font-heading font-bold text-xl">{lead.name}</div>
            <div className="text-white/50 text-sm">{lead.email}</div>
          </div>
          <button data-testid="drawer-close" onClick={onClose} className="text-white/70 hover:text-white"><X /></button>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Info label="Phone" value={lead.phone || "—"} />
            <Info label="Company" value={lead.company || "—"} />
            <Info label="Interest" value={lead.interest || "—"} />
            <Info label="Source" value={lead.source} />
          </div>
          {lead.message && (
            <div className="bg-[#F9FAFB] border border-[#E5E7EB] p-4 text-sm text-rc-brown">{lead.message}</div>
          )}
          {lead.attachment_url && (
            <a href={`${process.env.REACT_APP_BACKEND_URL}${lead.attachment_url}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-rc-red font-semibold">
              <FileText size={15} /> View bid attachment
            </a>
          )}

          {/* Status */}
          <div>
            <Label>Status</Label>
            <select
              data-testid="drawer-status-select"
              value={lead.status}
              onChange={(e) => onUpdate(lead.id, { status: e.target.value })}
              className="mt-2 w-full border border-[#E5E7EB] px-3 py-2.5 text-sm outline-none focus:border-rc-red"
            >
              {["new", "contacted", "qualified", "won", "lost"].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {/* Insight */}
          <div>
            <Label>Browsing Insight</Label>
            <div className="mt-2 grid grid-cols-2 gap-3">
              <div className="bg-rc-red/5 border border-rc-red/20 p-3">
                <div className="flex items-center gap-1.5 text-xs text-rc-red font-mono"><Eye size={12} /> Sections</div>
                <div className="mt-1 text-xs text-rc-charcoal">
                  {summary.top_sections.length ? summary.top_sections.map((s) => s.label).join(", ") : "—"}
                </div>
              </div>
              <div className="bg-rc-red/5 border border-rc-red/20 p-3">
                <div className="flex items-center gap-1.5 text-xs text-rc-red font-mono"><MousePointerClick size={12} /> Interests</div>
                <div className="mt-1 text-xs text-rc-charcoal">
                  {summary.top_services.length ? summary.top_services.map((s) => s.label).join(", ") : "—"}
                </div>
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div>
            <Label>Activity Timeline ({events.length})</Label>
            <div className="mt-2 space-y-2 max-h-72 overflow-y-auto rc-chat-scroll">
              {events.map((e, i) => (
                <div key={i} className="flex gap-3 text-xs border-l-2 border-rc-sand pl-3 py-1">
                  <span className="text-gray-400 font-mono shrink-0">{fmt(e.ts).split(",")[1]?.trim()}</span>
                  <span className="text-rc-charcoal">
                    <b>{e.type}</b> {e.label || e.path || ""}
                  </span>
                </div>
              ))}
              {events.length === 0 && <div className="text-gray-400 text-xs">No tracked activity for this lead.</div>}
            </div>
          </div>

          {appointments.length > 0 && (
            <div>
              <Label>Appointments</Label>
              {appointments.map((a) => (
                <div key={a.id} className="mt-2 bg-[#F9FAFB] border border-[#E5E7EB] p-3 text-sm">
                  {a.preferred_date} {a.preferred_time} — <span className="text-gray-500">{a.status}</span>
                </div>
              ))}
            </div>
          )}

          {/* Notes */}
          <div>
            <Label>Internal Notes</Label>
            <textarea
              data-testid="drawer-notes"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="mt-2 w-full border border-[#E5E7EB] px-3 py-2.5 text-sm outline-none focus:border-rc-red resize-none"
            />
            <button
              data-testid="drawer-save-notes"
              onClick={() => onUpdate(lead.id, { notes })}
              className="mt-2 bg-rc-charcoal text-white px-5 py-2.5 text-xs font-bold uppercase tracking-wide hover:bg-rc-red transition-colors"
            >
              Save Notes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const Label = ({ children }) => (
  <div className="font-mono text-[11px] uppercase tracking-[0.15em] text-gray-500">{children}</div>
);
const Info = ({ label, value }) => (
  <div>
    <Label>{label}</Label>
    <div className="mt-1 text-rc-charcoal font-medium">{value}</div>
  </div>
);


function SiteContentTab({ content, onSave }) {
  const [form, setForm] = useState(content || {});
  useEffect(() => setForm(content || {}), [content]);
  const setNested = (group, key, value) => setForm((f) => ({ ...f, [group]: { ...(f[group] || {}), [key]: value } }));
  const save = () => onSave(form);
  return (
    <div data-testid="content-tab">
      <h1 className="font-heading font-black text-3xl text-rc-charcoal mb-1">Site Content</h1>
      <p className="text-gray-500 mb-8">Manage front-end content defaults, contact info, social links, and latest news.</p>
      <div className="grid lg:grid-cols-2 gap-6">
        <AdminPanel title="Brand + Latest">
          <Field label="Brand Name" value={form.brand?.name || ""} onChange={(v) => setNested("brand", "name", v)} />
          <Field label="Tagline" value={form.brand?.tagline || ""} onChange={(v) => setNested("brand", "tagline", v)} />
          <Field label="Latest Headline" value={form.latest?.headline || ""} onChange={(v) => setNested("latest", "headline", v)} />
          <Field label="Latest Body" textarea value={form.latest?.body || ""} onChange={(v) => setNested("latest", "body", v)} />
          <Field label="CTA Label" value={form.latest?.cta_label || ""} onChange={(v) => setNested("latest", "cta_label", v)} />
          <Field label="CTA URL" value={form.latest?.cta_url || ""} onChange={(v) => setNested("latest", "cta_url", v)} />
        </AdminPanel>
        <AdminPanel title="Contact + Social">
          <Field label="HQ Phone" value={form.contact?.hq_phone || ""} onChange={(v) => setNested("contact", "hq_phone", v)} />
          <Field label="HQ Address" value={form.contact?.hq_address || ""} onChange={(v) => setNested("contact", "hq_address", v)} />
          <Field label="General Email" value={form.contact?.general_email || ""} onChange={(v) => setNested("contact", "general_email", v)} />
          <Field label="Estimating Email" value={form.contact?.estimating_email || ""} onChange={(v) => setNested("contact", "estimating_email", v)} />
          {['facebook','linkedin','instagram','youtube','x'].map((k) => (
            <Field key={k} label={k} value={form.social_links?.[k] || ""} onChange={(v) => setNested("social_links", k, v)} />
          ))}
        </AdminPanel>
      </div>
      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <CollectionEditor
          title="Project Portfolio"
          items={form.projects || []}
          fields={["title", "location", "type", "category", "img"]}
          onChange={(projects) => setForm((current) => ({ ...current, projects }))}
        />
        <CollectionEditor
          title="News Updates"
          items={form.news || []}
          fields={["title", "summary", "category"]}
          onChange={(news) => setForm((current) => ({ ...current, news }))}
        />
      </div>
      <button onClick={save} className="mt-6 bg-rc-red text-white px-6 py-3 text-sm font-bold uppercase tracking-wide hover:bg-rc-reddark">Save Content</button>
    </div>
  );
}

function CollectionEditor({ title, items, fields, onChange }) {
  const updateItem = (index, key, value) => onChange(items.map((item, itemIndex) => itemIndex === index ? { ...item, [key]: value } : item));
  const removeItem = (index) => onChange(items.filter((_, itemIndex) => itemIndex !== index));
  const addItem = () => onChange([...items, Object.fromEntries(fields.map((field) => [field, ""]))]);
  return (
    <AdminPanel title={title}>
      <div className="space-y-4">
        {items.map((item, index) => (
          <div key={`${title}-${index}`} className="border border-[#E5E7EB] p-4 space-y-3">
            {fields.map((field) => <Field key={field} label={field} textarea={field === "summary"} value={item[field] || ""} onChange={(value) => updateItem(index, field, value)} />)}
            <button type="button" onClick={() => removeItem(index)} className="text-xs font-bold uppercase text-rc-red">Remove</button>
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-gray-400">Using the built-in published defaults.</p>}
        <button type="button" onClick={addItem} className="inline-flex items-center gap-2 border border-rc-red text-rc-red px-4 py-2 text-xs font-bold uppercase"><Plus size={14}/> Add item</button>
      </div>
    </AdminPanel>
  );
}

function VanguardTab({ issues, onCreate, onUpdate, onUploadCover }) {
  const empty = { title: "", period: "", issue_no: "", summary: "", read_more_url: "", cover_url: "", is_latest: false };
  const [form, setForm] = useState(empty);
  const [drafts, setDrafts] = useState({});
  const [expanded, setExpanded] = useState({});
  useEffect(() => {
    const next = {};
    issues.forEach((i) => { next[i.id] = { title: i.title || "", period: i.period || "", summary: i.summary || "", read_more_url: i.read_more_url || i.pdf_url || "", cover_url: i.cover_url || "" }; });
    setDrafts(next);
  }, [issues]);
  const submit = async (e) => { e.preventDefault(); await onCreate(form); setForm(empty); };
  const setDraft = (id, key, value) => setDrafts((d) => ({ ...d, [id]: { ...(d[id] || {}), [key]: value } }));
  return (
    <div data-testid="vanguard-tab">
      <h1 className="font-heading font-black text-3xl text-rc-charcoal mb-1">The Vanguard</h1>
      <p className="text-gray-500 mb-8">Manage the latest issue card and expandable past issues.</p>
      <AdminPanel title="Add External Issue">
        <form onSubmit={submit} className="grid md:grid-cols-2 gap-3">
          <Field label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} required />
          <Field label="Period" value={form.period} onChange={(v) => setForm({ ...form, period: v })} />
          <Field label="Issue No." value={form.issue_no} onChange={(v) => setForm({ ...form, issue_no: v })} />
          <Field label="Cover Image URL" value={form.cover_url} onChange={(v) => setForm({ ...form, cover_url: v })} />
          <div className="md:col-span-2"><Field label="Read More URL" value={form.read_more_url} onChange={(v) => setForm({ ...form, read_more_url: v })} required /></div>
          <div className="md:col-span-2"><Field label="Summary" textarea value={form.summary} onChange={(v) => setForm({ ...form, summary: v })} /></div>
          <label className="flex items-center gap-2 text-sm text-rc-charcoal"><input type="checkbox" checked={form.is_latest} onChange={(e) => setForm({ ...form, is_latest: e.target.checked })} /> Mark as latest issue</label>
          <button className="bg-rc-red text-white px-5 py-2.5 text-sm font-bold uppercase">Add Issue</button>
        </form>
      </AdminPanel>
      <div className="mt-6 space-y-3">
        {issues.map((i) => {
          const d = drafts[i.id] || {};
          const open = !!expanded[i.id];
          return (
            <div key={i.id} className="bg-white border border-[#E5E7EB] rounded-md overflow-hidden">
              <button type="button" onClick={() => setExpanded((x) => ({ ...x, [i.id]: !x[i.id] }))} className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-50">
                <div><div className="font-semibold text-rc-charcoal">{i.title}</div><div className="text-xs text-gray-400">{i.period || i.issue_no} {i.is_latest ? '· Latest' : ''}</div></div>
                <ChevronRight size={18} className={`transition-transform ${open ? 'rotate-90' : ''}`} />
              </button>
              {open && (
                <div className="border-t border-[#E5E7EB] p-5 grid lg:grid-cols-[120px,1fr] gap-5">
                  <div>
                    {d.cover_url ? <img src={d.cover_url.startsWith('http') ? d.cover_url : `${process.env.REACT_APP_BACKEND_URL}${d.cover_url}`} alt="cover" className="w-full rounded border border-[#E5E7EB]" /> : <div className="aspect-[0.77] bg-gray-100 rounded" />}
                    <input type="file" accept="image/*" className="mt-3 text-xs" onChange={(e) => e.target.files?.[0] && onUploadCover(i.id, e.target.files[0])} />
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    <Field label="Title" value={d.title || ""} onChange={(v) => setDraft(i.id, 'title', v)} />
                    <Field label="Period" value={d.period || ""} onChange={(v) => setDraft(i.id, 'period', v)} />
                    <div className="md:col-span-2"><Field label="Read More URL" value={d.read_more_url || ""} onChange={(v) => setDraft(i.id, 'read_more_url', v)} /></div>
                    <div className="md:col-span-2"><Field label="Cover Image URL" value={d.cover_url || ""} onChange={(v) => setDraft(i.id, 'cover_url', v)} /></div>
                    <div className="md:col-span-2"><Field label="Summary" textarea value={d.summary || ""} onChange={(v) => setDraft(i.id, 'summary', v)} /></div>
                    <label className="flex items-center gap-2 text-sm text-rc-charcoal"><input type="checkbox" checked={!!i.is_latest} onChange={(e) => onUpdate(i.id, { is_latest: e.target.checked })} /> Latest</label>
                    <button onClick={() => onUpdate(i.id, d)} className="bg-rc-charcoal text-white px-5 py-2.5 text-sm font-bold uppercase">Save Issue</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {issues.length === 0 && <div className="bg-white border border-[#E5E7EB] p-6 text-gray-400">No issues yet.</div>}
      </div>
    </div>
  );
}

function EditRequestsTab({ requests, onUpdate, onCreate }) {
  const [mode, setMode] = useState("open");
  const [showNew, setShowNew] = useState(false);
  const empty = { page_option: "Home", new_page_title: "", section: "", body: "", notes: "" };
  const [form, setForm] = useState(empty);
  const [attachment, setAttachment] = useState(null);
  const closedStatuses = ["completed", "rejected"];
  const visible = requests.filter((r) => mode === "closed" ? closedStatuses.includes(r.status) : !closedStatuses.includes(r.status));
  const updateForm = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    const data = new FormData();
    data.append("page_option", form.page_option);
    data.append("page", form.page_option === "New Page" ? form.new_page_title : form.page_option);
    data.append("new_page_title", form.new_page_title || "");
    data.append("section", form.section || "");
    data.append("body", form.body || "");
    data.append("notes", form.notes || "");
    if (attachment) data.append("attachment", attachment);
    await onCreate(data);
    setForm(empty);
    setAttachment(null);
    setShowNew(false);
  };
  return (
    <div data-testid="edit-requests-tab">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading font-black text-3xl text-rc-charcoal mb-1">Website Edit Requests</h1>
          <p className="text-gray-500">Requests submitted from the public site for copy/content edits.</p>
        </div>
        <button onClick={() => setShowNew((v) => !v)} className="inline-flex items-center gap-2 bg-rc-red text-white px-4 py-2 text-sm font-bold uppercase"><Plus size={15} /> Add Request</button>
      </div>
      <div className="flex gap-2 mb-5">
        <button onClick={() => setMode("open")} className={`px-4 py-2 text-sm font-bold uppercase border ${mode === 'open' ? 'bg-rc-charcoal text-white border-rc-charcoal' : 'bg-white border-[#E5E7EB]'}`}>Open Requests</button>
        <button onClick={() => setMode("closed")} className={`px-4 py-2 text-sm font-bold uppercase border ${mode === 'closed' ? 'bg-rc-charcoal text-white border-rc-charcoal' : 'bg-white border-[#E5E7EB]'}`}>Closed Requests</button>
      </div>
      {showNew && (
        <AdminPanel title="Add Website Edit Request">
          <form onSubmit={submit} className="grid md:grid-cols-2 gap-3">
            <label className="block"><Label>Page</Label><select value={form.page_option} onChange={(e) => updateForm('page_option', e.target.value)} className="mt-1 w-full border border-[#E5E7EB] px-3 py-2 text-sm outline-none focus:border-rc-red bg-white">{['Home','Services','Safety','FOB Asphalt Sales','Careers','Contact','The Vanguard','Footer','New Page','Other'].map((p) => <option key={p}>{p}</option>)}</select></label>
            <Field label="Section" value={form.section} onChange={(v) => updateForm('section', v)} />
            {form.page_option === 'New Page' && <div className="md:col-span-2"><Field label="New Page Title" value={form.new_page_title} onChange={(v) => updateForm('new_page_title', v)} /></div>}
            <div className="md:col-span-2"><Field label="Body" textarea required value={form.body} onChange={(v) => updateForm('body', v)} /></div>
            <div className="md:col-span-2"><Field label="Notes" textarea value={form.notes} onChange={(v) => updateForm('notes', v)} /></div>
            <label className="md:col-span-2 flex items-center justify-between gap-3 border border-dashed border-[#D1D5DB] px-4 py-3 text-sm text-rc-brown cursor-pointer hover:border-rc-red">
              <span className="inline-flex items-center gap-2"><ImageIcon size={16} /> {attachment ? attachment.name : "Attach file (screenshot, PDF, DOCX, XLSX, CSV)"}</span>
              <input type="file" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.rtf,.ppt,.pptx" className="hidden" onChange={(e) => setAttachment(e.target.files?.[0] || null)} />
            </label>
            <button type="submit" className="bg-rc-red text-white px-5 py-2.5 text-sm font-bold uppercase">Create Request</button>
          </form>
        </AdminPanel>
      )}
      <div className="space-y-4 mt-5">
        {visible.map((r) => (
          <div key={r.id} className="bg-white border border-[#E5E7EB] rounded-md p-5">
            <div className="flex items-start justify-between gap-4"><div><div className="font-heading font-bold text-rc-charcoal">{r.page || r.page_option || 'General'} {r.new_page_title && <span className="text-gray-400">/ New: {r.new_page_title}</span>} {r.section && <span className="text-gray-400">/ {r.section}</span>}</div><div className="text-xs text-gray-400 mt-1">{fmt(r.created_at)} · {r.name || 'Admin'} {r.email || ''}</div></div>
              <select value={r.status} onChange={(e) => onUpdate(r.id, { status: e.target.value })} className="border border-[#E5E7EB] px-2 py-1 text-xs"><option>new</option><option>reviewing</option><option>approved</option><option>completed</option><option>rejected</option></select>
            </div>
            <div className="mt-3 text-sm text-rc-charcoal whitespace-pre-wrap"><b>Body:</b> {r.body || r.requested_text}</div>
            {r.notes && <div className="mt-2 text-xs text-gray-500"><b>Notes:</b> {r.notes}</div>}
            {r.attachment_url && <a href={`${process.env.REACT_APP_BACKEND_URL}${r.attachment_url}`} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-2 text-sm text-rc-red font-semibold"><ImageIcon size={15} /> View attachment</a>}
          </div>
        ))}
        {visible.length === 0 && <div className="bg-white border border-[#E5E7EB] p-6 text-gray-400">No {mode} edit requests.</div>}
      </div>
    </div>
  );
}

function SettingsTab({ onConnected, currentEmail, onRemoveUser, users, alerts, constantContact, onCreateUser, onUpdateUser, onSaveAlerts, onSaveConstantContact }) {
  const [recipients, setRecipients] = useState("");
  const [includeSummary, setIncludeSummary] = useState(true);
  const [section, setSection] = useState("contact");
  const [message, setMessage] = useState("");
  const [ccForm, setCcForm] = useState({ enabled: false, sync_contact_forms: false, list_id: "", client_id: "", client_secret: "", refresh_token: "", access_token: "" });

  useEffect(() => {
    setRecipients((alerts?.appointment_recipients || []).join(", "));
    setIncludeSummary(alerts?.include_chatbot_summary ?? true);
  }, [alerts]);

  useEffect(() => {
    setCcForm((current) => ({ ...current, enabled: constantContact?.enabled ?? false, sync_contact_forms: constantContact?.sync_contact_forms ?? false, list_id: constantContact?.list_id || "", client_id: "", client_secret: "", refresh_token: "", access_token: "" }));
  }, [constantContact]);

  const saveAlerts = async (event) => {
    event.preventDefault();
    try {
      const appointment_recipients = recipients.split(/[\n,;]/).map((email) => email.trim()).filter(Boolean);
      await onSaveAlerts({ appointment_recipients, include_chatbot_summary: includeSummary });
      setMessage("Alert settings saved.");
    } catch (error) {
      setMessage(error?.response?.data?.detail || "Could not save alert settings.");
    }
  };

  const saveConstantContact = async (event) => {
    event.preventDefault();
    try {
      await onSaveConstantContact(ccForm);
      setCcForm((current) => ({ ...current, client_id: "", client_secret: "", refresh_token: "", access_token: "" }));
      setMessage("Constant Contact settings saved.");
    } catch (error) {
      setMessage(error?.response?.data?.detail || "Could not save Constant Contact settings.");
    }
  };

  return (
    <div data-testid="settings-tab">
      <h1 className="font-heading font-black text-3xl text-rc-charcoal mb-1">Settings</h1>
      <p className="text-gray-500 mb-8">Manage contact settings and administrator accounts.</p>
      {message && <div className="mb-5 border border-rc-red/30 bg-rc-red/5 px-4 py-3 text-sm text-rc-charcoal">{message}</div>}
      <nav aria-label="Settings sections" className="mb-6 flex gap-3">
        {[['contact', 'Contact'], ['users', 'User management']].map(([key, label]) => <button type="button" key={key} aria-pressed={section === key} onClick={() => { setSection(key); setMessage(""); }} className={`rounded border px-5 py-3 text-sm font-bold ${section === key ? "bg-rc-charcoal text-white" : "bg-white text-rc-charcoal"}`}>{label}</button>)}
      </nav>
      {section === "users" ? <UserManagement users={users} currentEmail={currentEmail} onCreate={onCreateUser} onUpdate={onUpdateUser} onRemove={onRemoveUser} /> : <div className="grid xl:grid-cols-2 gap-6">

        <AdminPanel title="Appointment Alerts">
          <form onSubmit={saveAlerts} className="space-y-4">
            <Field label="Notification recipients" textarea value={recipients} onChange={setRecipients} />
            <p className="text-xs text-gray-500 -mt-2">Enter one or more email addresses, separated by commas or new lines. These recipients receive every appointment request.</p>
            <label className="flex items-start gap-3 text-sm text-rc-charcoal cursor-pointer">
              <input type="checkbox" checked={includeSummary} onChange={(e) => setIncludeSummary(e.target.checked)} className="mt-1" />
              <span><b>Include chatbot conversation</b><br /><span className="text-gray-500">For chatbot-booked appointments, include the recent conversation with the request email.</span></span>
            </label>
            <button type="submit" className="bg-rc-red text-white px-5 py-2.5 text-sm font-bold uppercase">Save Alert Settings</button>
          </form>
        </AdminPanel>
        <AdminPanel title="Constant Contact">
          <ConstantContactConnection settings={constantContact} onConnected={onConnected} />
          <form onSubmit={saveConstantContact} className="space-y-4">
            <p className="text-sm text-gray-500">Sync newsletter subscribers, plus form submissions where the visitor has explicitly opted in, to one Constant Contact list.</p>
            <label className="flex items-center gap-3 text-sm text-rc-charcoal cursor-pointer"><input type="checkbox" checked={ccForm.enabled} onChange={(e) => setCcForm({ ...ccForm, enabled: e.target.checked })} /><span><b>Enable Constant Contact sync</b></span></label>
            <label className="flex items-start gap-3 text-sm text-rc-charcoal cursor-pointer"><input type="checkbox" checked={ccForm.sync_contact_forms} onChange={(e) => setCcForm({ ...ccForm, sync_contact_forms: e.target.checked })} /><span><b>Sync opted-in contact and request forms</b><br /><span className="text-gray-500">Newsletter signups always qualify; contact submissions only sync after the visitor selects the marketing opt-in.</span></span></label>
            <Field label="List ID" value={ccForm.list_id} onChange={(list_id) => setCcForm({ ...ccForm, list_id })} />
            <details className="rounded border border-gray-200 p-3">
              <summary className="cursor-pointer text-sm font-bold">App setup</summary>
              <div className="mt-4 space-y-4">
                <p className="text-sm text-gray-500">Register this exact redirect URL in your Constant Contact developer app:</p>
                <code className="block break-all text-xs">{constantContact?.redirect_uri || "https://ranger.vestademos.com/admin?cc_callback=1"}</code>
                <Field label={`Client ID${constantContact?.has_client_id ? " (saved)" : ""}`} value={ccForm.client_id} onChange={(client_id) => setCcForm({ ...ccForm, client_id })} />
                <label className="block"><Label>{`Client secret${constantContact?.has_client_secret ? " (saved)" : ""}`}</Label><input type="password" autoComplete="new-password" value={ccForm.client_secret} onChange={(e) => setCcForm({ ...ccForm, client_secret: e.target.value })} className="mt-1 w-full border border-[#E5E7EB] px-3 py-2 text-sm" /></label>
                <p className="text-xs text-gray-500">Save the app settings, then use Connect above. Access and refresh tokens are obtained automatically and kept on the server.</p>
              </div>
            </details>
            <button type="submit" className="bg-rc-charcoal text-white px-5 py-2.5 text-sm font-bold uppercase">Save Constant Contact</button>
          </form>
        </AdminPanel>
      </div>}
    </div>
  );
}

function AdminPanel({ title, children }) {
  return <div className="bg-white border border-[#E5E7EB] rounded-md p-5"><h2 className="font-heading font-bold text-xl text-rc-charcoal mb-4">{title}</h2><div className="space-y-3">{children}</div></div>;
}

function Field({ label, value, onChange, textarea, required }) {
  return <label className="block"><Label>{label}</Label>{textarea ? <textarea required={required} rows={4} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full border border-[#E5E7EB] px-3 py-2 text-sm outline-none focus:border-rc-red" /> : <input required={required} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full border border-[#E5E7EB] px-3 py-2 text-sm outline-none focus:border-rc-red" />}</label>;
}
